import { ApplicationConfig, provideZoneChangeDetection, isDevMode, enableProdMode, APP_INITIALIZER } from '@angular/core';
import { AuthService } from './core/auth/auth.service';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.routes';
import { provideServiceWorker } from '@angular/service-worker';
import { provideToastr } from 'ngx-toastr';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { environment } from '../environments/environments';
import { provideAnimations } from '@angular/platform-browser/animations';
import { authInterceptor } from './interceptors/auth.interceptor';
import { errorInterceptor } from './interceptors/error.interceptor';
import { loadingInterceptor } from './interceptors/loading.interceptor';
import { trackingInterceptor } from './interceptors/tracking.interceptor';
import { Ability, PureAbility } from '@casl/ability';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { CUSTOM_DATE_FORMATS } from './data-models/my-date-formats';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { LocaleDateAdapter } from './helpers/locale-date-adapter';
// Side-effect import: registers Chart.js + datalabels and sets global chart defaults
// (no permanent data labels, shared modern tooltip) once, at app startup.
import './helpers/chart-theme';


if (environment.production) {
  enableProdMode();
}
export const appConfig: ApplicationConfig = {
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: DateAdapter, useClass: LocaleDateAdapter },
    { provide: Ability, useValue: new Ability() },
    { provide: PureAbility, useExisting: Ability },
    provideZoneChangeDetection(
      { eventCoalescing: true }),
      provideRouter(routes, withInMemoryScrolling({
        scrollPositionRestoration: 'enabled', // Scroll to the top on navigation
        anchorScrolling: 'enabled' // Optional: enable anchor scrolling
      })),
      provideServiceWorker('custom-service-worker.js', {
        enabled: !isDevMode(),
        // registrationStrategy: 'registerWhenStable:30000'
        registrationStrategy: 'registerImmediately'
    }),
    // Order is outer -> inner. errorInterceptor is outermost so it observes the
    // final outcome (after auth's 401 refresh+retry); authInterceptor is
    // innermost so its retry re-runs only the network leg and loading counts
    // one logical request.
    provideHttpClient(
      withInterceptors([errorInterceptor, loadingInterceptor, trackingInterceptor, authInterceptor]),
    ),
    // On a fresh load the in-memory access token is gone but the refresh token
    // persists — refresh once up front so initial protected calls don't 401 en
    // masse. Failures are swallowed (the normal 401 path handles re-auth).
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (auth: AuthService) => () => auth.tryEagerRefresh(),
      deps: [AuthService],
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAnimations(),
    provideToastr(),
    provideAnimationsAsync(),
    provideAnimationsAsync(),
    provideCharts(withDefaultRegisterables()),
  ]
};
